import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import useNavigationStructureStore from './navigation-structure';
import type { EnhancedPage, EnhancedTocItem } from '../types/navigation';
import { findPageIndexByCfi } from '../util/navigation/page-utils';

/**
 * PositionTrackingStore
 *
 * Single source of truth for the current position in the book.
 * The primary position state is the spine index.
 *
 * Level 1 in dependency hierarchy - depends only on NavigationStructureStore.
 */
const usePositionTrackingStore = defineStore('book-player-position-tracking', () => {
  // Primary position state - spine index is the source of truth
  const currentSpineIndex = ref<number>(0);

  // Current page index - directly tracked for Reader view
  const currentPageIndex = ref<number>(0);

  // Current position as a percentage (0-100)
  const currentPositionPct = ref<number>(0);

  // Optional: Store CFI for precision within the spine item
  const currentCFI = ref<string | undefined>(undefined);

  // Total number of spine items (set during initialization)
  const totalSpineItems = ref<number>(0);

  // Audio-based position tracking (for books with audio narration)
  const currentAudioTrackHref = ref<string | undefined>(undefined);
  const currentAudioTrackCFI = ref<string | undefined>(undefined);

  // Playlist position tracking (for audio-only books)
  const currentPlaylistIndex = ref<number>(0);
  const currentTrackIndexInItem = ref<number>(0);
  const currentPlaylistTime = ref<number>(0); // Current time in seconds within the playlist

  // Get the navigation structure store (Level 0 dependency)
  const { pages } = storeToRefs(useNavigationStructureStore());
  const { getActiveTocHrefByHref, getTocByHref, getChapterTitleForHref } = useNavigationStructureStore();

  /**
   * Derived: Current page object
   */
  const currentPage = computed((): EnhancedPage | null => {
    return pages.value[currentPageIndex.value] || null;
  });

  /**
   * Derived: Current ToC item
   * Prioritizes audio track position if available
   */
  const currentTocItem = computed((): EnhancedTocItem | null => {
    // For audio books, use audio track position
    if (currentAudioTrackHref.value) {
      // Try exact match with full href (includes fragment)
      const item = getTocByHref(currentAudioTrackHref.value);
      if (item) return item;

      // Try base href as fallback
      const [baseHref] = currentAudioTrackHref.value.split('#');
      const baseItem = getTocByHref(baseHref);
      if (baseItem) return baseItem;
    }

    // Fall back to visual page position
    if (!currentPage.value?.href) return null;

    // Construct full href with fragment if page has an id
    const fullHref = currentPage.value.id
      ? `${currentPage.value.href}#${currentPage.value.id}`
      : currentPage.value.href;

    // Try to match the full href first (with fragment if present)
    // This allows matching specific ToC subitems
    if (fullHref !== currentPage.value.href) {
      const itemWithFragment = getTocByHref(fullHref);
      if (itemWithFragment) return itemWithFragment;
    }

    // Try the tocDependencyMap which maps pages to their parent ToC items
    const tocHref = getActiveTocHrefByHref(currentPage.value.href);
    if (tocHref) {
      return getTocByHref(tocHref) || null;
    }

    // Fall back to base href matching
    return getTocByHref(currentPage.value.href) || null;
  });

  /**
   * Derived: Current chapter title
   * Prioritizes audio track position if available, otherwise uses visual page position
   */
  const currentChapterTitle = computed((): string => {
    // For books with audio, use the audio track's position
    if (currentAudioTrackHref.value) {
      // First try the FULL href (with fragment) to get specific subitems
      const fullHrefTitle = getChapterTitleForHref(currentAudioTrackHref.value);
      if (fullHrefTitle) return fullHrefTitle;

      // Fall back to base href (without fragment) if full href doesn't match
      const [href] = currentAudioTrackHref.value.split('#');
      return getChapterTitleForHref(href);
    }

    // Fall back to visual page position
    if (!currentPage.value?.href) return '';

    // Construct full href with fragment if page has an id
    const fullHref = currentPage.value.id
      ? `${currentPage.value.href}#${currentPage.value.id}`
      : currentPage.value.href;

    // Try to get the title with the full href first (with fragment if present)
    const fullHrefTitle = getChapterTitleForHref(fullHref);
    if (fullHrefTitle) return fullHrefTitle;

    // Fall back to base href
    return getChapterTitleForHref(currentPage.value.href);
  });

  /**
   * Set the current position by spine index (primary method)
   */
  const setSpineIndex = (index: number) => {
    if (index >= 0 && index !== currentSpineIndex.value) {
      currentSpineIndex.value = index;

      // Note: We do NOT auto-mirror to currentPageIndex for audio-only books (pages.length === 0)
      // In audio-only mode, currentPageIndex represents the playlist index (sequential)
      // while currentSpineIndex is just metadata (non-sequential)
      // The playlist store manages currentPageIndex directly via setCurrentPageIndex
    }
  };

  /**
   * Set the current position percentage (0-100)
   */
  const setPositionPct = (percentage: number) => {
    if (percentage >= 0 && percentage <= 100) {
      currentPositionPct.value = percentage;
    }
  };

  /**
   * Set the current audio track position (for audio narration)
   * @param href - The href of the currently playing/queued audio track
   * @param cfi - Optional CFI for precise position
   */
  const setAudioTrackPosition = (href: string | undefined, cfi?: string) => {
    currentAudioTrackHref.value = href;
    currentAudioTrackCFI.value = cfi;
  };

  /**
   * Set playlist position (for audio-only books)
   * @param playlistIndex - Current playlist item index
   * @param trackIndex - Current track index within the playlist item
   * @param time - Current playback time in seconds
   */
  const setPlaylistPosition = (playlistIndex: number, trackIndex: number = 0, time: number = 0) => {
    if (playlistIndex >= 0) {
      currentPlaylistIndex.value = playlistIndex;
      currentTrackIndexInItem.value = trackIndex;
      currentPlaylistTime.value = time;
    }
  };

  /**
   * Set the current CFI (called on every position update)
   */
  const setCFI = (cfi: string) => {
    currentCFI.value = cfi;
  };

  /**
   * Set the current position by CFI and update page index
   */
  const setFromCFI = (cfi: string, compareFn: (a: string, b: string) => number) => {
    currentCFI.value = cfi;

    // Update page index based on CFI
    if (pages.value.length === 0) return;

    const pageIndex = getPageIndexByCfi(cfi, compareFn);
    if (pageIndex !== -1) {
      currentPageIndex.value = pageIndex;
    }
  };

  /**
   * Get current CFI
   */
  const getCurrentCFI = computed((): string => {
    return currentCFI.value || '';
  });

  /**
   * Find page index by CFI comparison
   */
  const getPageIndexByCfi = (targetCfi: string, compareFn: (a: string, b: string) => number): number => {
    return findPageIndexByCfi(pages.value, targetCfi, compareFn);
  };

  /**
   * Set the current page index directly
   * For audio-only books (no pages), this represents the playlist index
   */
  const setCurrentPageIndex = (index: number) => {
    if (index >= 0) {
      // Allow setting even when pages.length === 0 (for audio-only books where this represents playlist index)
      if (pages.value.length === 0 || index < pages.value.length) {
        currentPageIndex.value = index;
      }
    }
  };

  /**
   * Get a page number for a specific spine index
   */
  const getPageNumberForSpineIndex = (spineIndex: number): number | null => {
    if (!pages || pages.value.length === 0) return null;

    // Find the page that corresponds to this spine index
    const page = pages.value[spineIndex];
    return page?.page || null;
  };

  /**
   * Get a chapter title for a specific spine index
   */
  const getChapterTitleForSpineIndex = (spineIndex: number): string => {
    if (!pages.value || pages.value.length === 0) return '';

    const page = pages.value[spineIndex];
    if (!page?.href) return '';

    return getChapterTitleForHref(page.href);
  };

  /**
   * Set total spine items (called during initialization)
   */
  const setTotalSpineItems = (total: number) => {
    totalSpineItems.value = total;
  };

  const $reset = () => {
    currentSpineIndex.value = 0;
    currentPageIndex.value = 0;
    currentPositionPct.value = 0;
    currentCFI.value = undefined;
    currentAudioTrackHref.value = undefined;
    currentAudioTrackCFI.value = undefined;
    totalSpineItems.value = 0;
    currentPlaylistIndex.value = 0;
    currentTrackIndexInItem.value = 0;
    currentPlaylistTime.value = 0;
  };

  return {
    currentSpineIndex,
    currentCFI,
    currentPageIndex,
    currentPositionPct,
    currentAudioTrackHref,
    currentAudioTrackCFI,
    currentPlaylistIndex,
    currentTrackIndexInItem,
    currentPlaylistTime,
    currentPage,
    currentTocItem,
    currentChapterTitle,
    totalSpineItems,
    getCurrentCFI,
    setSpineIndex,
    setPositionPct,
    setCFI,
    setAudioTrackPosition,
    setPlaylistPosition,
    setFromCFI,
    setCurrentPageIndex,
    setTotalSpineItems,
    getPageIndexByCfi,
    getPageNumberForSpineIndex,
    getChapterTitleForSpineIndex,
    $reset,
  };
});

export default usePositionTrackingStore;
